/* @flow */

import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';
import { observer } from 'mobx-react';
import * as React from 'react';
import { observable } from 'mobx';
import Moment from 'react-moment';
import Collapse from '@material-ui/core/Collapse';
import { Link } from 'react-router-dom';
import Button from '@material-ui/core/Button';

import {
  AssetDetails,
  ContractDetails,
  AssetRequestDetails,
  LicenseRequestDetails,
} from 'components/SentRequestTableItem/SentRequestTableItem';
import { generateValues, getNotificationCopy, SENT_REQUEST_TYPES } from 'utils/NotificationUtils';
import { errorHandlerService } from 'services/ServiceRegistry';
import { ComponentWithInjectedProps } from 'utils/injectedProps';

import styles from '../NotificationPanel/NotificationPanel.module.scss';

export type SentRequestPanelProps = {
  sentRequest: Object,
};

type InjectedProps = TranslatorProps;

const variantIcon = {
  approved: '#icon-approve',
  declined: '#icon-declined',
  request: '#icon-archive',
  request_rental_extension: '#icon-extend-rental',
  info: '#icon-information',
  warning: '#icon-error',
};

@translate()
@observer
export default class SentRequestPanel extends ComponentWithInjectedProps<
  SentRequestPanelProps,
  {},
  InjectedProps
> {
  @observable isExpanded = true;

  renderSRinfo = (actionName: string) => {
    try {
      const { t, sentRequest } = this.props;

      switch (actionName) {
        case SENT_REQUEST_TYPES.CONTRACT_EXTENSION:
          return <ContractDetails t={t} notification={sentRequest} />;
        case SENT_REQUEST_TYPES.SUPPORT_REQUEST:
          return null;
        case SENT_REQUEST_TYPES.SUBRENTAL_PROGRAM:
        case SENT_REQUEST_TYPES.CONSIGNMENT_SALE:
          return (
            <AssetDetails t={t} notification={sentRequest} asset={sentRequest.details.asset} />
          );
        case SENT_REQUEST_TYPES.ASSET_REQUEST:
          return <AssetRequestDetails t={t} notification={sentRequest} />;
        case SENT_REQUEST_TYPES.RUSH_ASSET_REQUEST:
          return <AssetRequestDetails t={t} notification={sentRequest} />;
        case SENT_REQUEST_TYPES.NEW_LICENSE_REQUEST:
          return <LicenseRequestDetails t={t} notification={sentRequest} />;
        default:
          const asset = sentRequest.details.contract.assets[0];
          return <AssetDetails t={t} notification={sentRequest} asset={asset} />;
      }
    } catch (err) {
      errorHandlerService.handleException(err);
    }
  };

  render() {
    const { t, sentRequest } = this.props;
    const isClickable = true;
    const hasButtons = true;
    const eventCategory = 'info';
    const Icon = variantIcon[eventCategory];

    const { notification } = generateValues(sentRequest);
    const titleCopyInfo = getNotificationCopy(notification, 'Title');

    return (
      <li className={`${styles['notification-panel']} ${styles[eventCategory]}`}>
        <div
          className={`${styles['notification-content']} ${
            isClickable ? styles['is-clickable'] : ''
          }`}
          onClick={() => {
            this.isExpanded = hasButtons ? !this.isExpanded : this.isExpanded;
          }}
        >
          <div className={`${styles['notification-header']} ${styles['sent-request-row-header']}`}>
            <div className={styles['noti-item-icon']}>
              <svg>
                <use xlinkHref="#icon-assets" />
              </svg>
              <svg className={`${styles['status-icon']} ${styles['status-requested']}`}>
                <use xlinkHref="#icon-notification-action" />
              </svg>
            </div>
            <div className={styles['notification-title']}>
              <h3>{t(titleCopyInfo.key, titleCopyInfo.options)}</h3>
              <span>
                &nbsp;-&nbsp;
                <Moment date={sentRequest.created_at} format="MMMM DD, YYYY - hh:mm a" />
              </span>
            </div>
            {hasButtons && (
              <div className={styles['expand-icon']}>
                <svg>
                  <use xlinkHref={this.isExpanded ? '#icon-chevron-down' : '#icon-chevron-up'} />
                </svg>
              </div>
            )}
          </div>
          <div className={styles['notification-body']}>
            <div className={styles['desc-bottom-line']}>
              {this.renderSRinfo(sentRequest.action_name)}

              {sentRequest.details.help_with ? (
                <div>
                  {t('general:notifications.requested-support-for')}:{' '}
                  {sentRequest.details.help_with}
                </div>
              ) : null}
            </div>
          </div>
        </div>
        <Collapse
          in={this.isExpanded}
          timeout="auto"
          unmountOnExit={true}
          className={styles['notification-button-bar']}
        >
          <div className={styles['button-container']}>
            <Link to={`/sent-requests/${sentRequest.id}`}>
              <Button variant="contained"> {t('general:actions.review')}</Button>
            </Link>
          </div>
        </Collapse>
      </li>
    );
  }
}
